import React, { useState } from 'react';
import { 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  IconButton,
  Box,
  CircularProgress,
  Typography,
  Button,
  Alert
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { trackDownload } from '../services/analytics';

const BACKEND_API = process.env.REACT_APP_API_URL || 'http://localhost:8000';

function PDFPreview({ open, onClose, documentUrl, title }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Use proxy to avoid CORS issues
  const proxyUrl = `${BACKEND_API}/proxy?url=${encodeURIComponent(documentUrl)}`;

  const handleLoad = () => {
    setLoading(false);
    setError(null);
  };

  const handleError = () => {
    setLoading(false);
    setError('Failed to load PDF. The server may be blocking access.');
  };

  const handleDownload = () => {
    window.open(documentUrl, '_blank');
  };

  const handleClose = () => {
    setLoading(true);
    setError(null);
    onClose();
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        className: 'glass-dark',
        sx: { height: '90vh', maxHeight: '90vh' }
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
        <Typography variant="body1" sx={{ pr: 2, fontWeight: 600 }} noWrap>
          {title}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            size="small"
            startIcon={<OpenInNewIcon />}
            onClick={handleDownload}
            sx={{ color: '#009688' }}
          >
            Open in New Tab
          </Button>
          <IconButton onClick={handleClose} sx={{ color: '#009688' }}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', p: 0, overflow: 'hidden', position: 'relative' }}>
        {error && (
          <Alert severity="warning" sx={{ m: 2, width: 'calc(100% - 32px)' }}>
            {error}
            <Button size="small" onClick={handleDownload} sx={{ ml: 2 }}>
              Open PDF
            </Button>
          </Alert>
        )}

        {loading && !error && (
          <Box sx={{ 
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center', 
            gap: 2,
            zIndex: 10
          }}>
            <CircularProgress sx={{ color: '#009688' }} />
            <Typography variant="body2" color="text.secondary">
              Loading PDF...
            </Typography>
          </Box>
        )}
        
        {!error && (
          <iframe
            src={proxyUrl}
            style={{
              width: '100%',
              height: '100%',
              border: 'none',
              minHeight: 'calc(90vh - 80px)'
            }}
            title={title}
            onLoad={handleLoad}
            onError={handleError}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

export default PDFPreview;
